import * as Clipboard from "expo-clipboard";
import { Check, Copy } from "lucide-react-native";
import { FC, useState } from "react";
import Markdown from "react-native-markdown-display";

import { Box, Pressable, Skeleton, Text, VStack } from "@/components/ui";
import { useLocalization } from "@/hooks";
import { Summary as SummaryData } from "@/types/resources";

/* ── Types ─────────────────────────────────────────────── */

interface SummaryProps {
  summary: SummaryData | null;
}

/* ── Styles ─────────────────────────────────────────────── */

const markdownStyles = {
  body: {
    color: "rgba(255,255,255,0.75)",
    fontSize: 14,
    lineHeight: 22,
  },
  strong: {
    color: "rgba(255,255,255,0.95)",
    fontWeight: "600" as const,
  },
  paragraph: {
    marginBottom: 10,
    marginTop: 0,
  },
};

/* ── Skeleton ───────────────────────────────────────────── */

export const SummarySkeleton: FC<{ generating?: boolean }> = ({
  generating = false,
}) => {
  const { i18n } = useLocalization();
  return (
    <Box
      className="rounded-2xl p-6"
      style={{ backgroundColor: "hsl(222, 24%, 12%)" }}
    >
      <VStack className="gap-3">
        {generating && (
          <Text
            className="text-sm font-medium"
            style={{ color: "rgba(255,255,255,0.5)" }}
          >
            {i18n.t("misc.generatingSummary")}
          </Text>
        )}
        <Skeleton className="h-3 w-24 rounded opacity-30" />
        <Skeleton className="h-4 w-full rounded opacity-20" />
        <Skeleton className="h-4 w-5/6 rounded opacity-20" />
        <Skeleton className="h-4 w-4/6 rounded opacity-20" />
      </VStack>
    </Box>
  );
};

/* ── Empty state ────────────────────────────────────────── */

// Shown when the question has no respondents: same dark "Key Insights" shell
// as the real summary, but with a plain "No data available" body.
export const SummaryEmpty: FC = () => {
  const { i18n } = useLocalization();
  return (
    <Box
      className="rounded-2xl p-6"
      style={{ backgroundColor: "hsl(222, 24%, 12%)" }}
    >
      <VStack className="gap-3">
        <Text
          className="text-xs font-semibold tracking-widest uppercase"
          style={{ color: "rgba(255,255,255,0.4)", letterSpacing: 2 }}
        >
          {i18n.t("dashboardNavigator.keyInsights")}
        </Text>
        <Text className="text-sm" style={{ color: "rgba(255,255,255,0.45)" }}>
          {i18n.t("dashboardNavigator.noData")}
        </Text>
      </VStack>
    </Box>
  );
};

/* ── Component ──────────────────────────────────────────── */

const Summary: FC<SummaryProps> = ({ summary }) => {
  const { i18n } = useLocalization();
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!summary) return null;

  const headline = summary.headline;
  const text: string =
    summary.summary ?? i18n.t("misc.failedToGenerateSummary");
  const isTruncatable = text.length > 700;
  const copyText = [headline, text].filter(Boolean).join("\n\n");

  const handleCopy = async () => {
    await Clipboard.setStringAsync(copyText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box
      className="relative group rounded-2xl p-6 lg:p-8"
      style={{ backgroundColor: "hsl(222, 24%, 12%)" }}
    >
      {/* Copy button — appears on hover */}
      <Box className="absolute top-4 right-4 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
        <Pressable onPress={handleCopy}>
          <Box
            className="p-1.5 rounded-lg"
            style={{ backgroundColor: "rgba(255,255,255,0.08)" }}
          >
            {copied ? (
              <Check color="rgba(255,255,255,0.6)" size={14} />
            ) : (
              <Copy color="rgba(255,255,255,0.4)" size={14} />
            )}
          </Box>
        </Pressable>
      </Box>

      <VStack className="gap-3">
        <Text
          className="text-xs font-semibold tracking-widest uppercase"
          style={{ color: "rgba(255,255,255,0.4)", letterSpacing: 2 }}
        >
          {i18n.t("dashboardNavigator.keyInsights")}
        </Text>

        {headline && (
          <Text
            className="text-3xl leading-snug mt-2 [font-family:Newsreader,Georgia,serif]"
            style={{ color: "rgba(255,255,255,0.95)" }}
          >
            {headline}
          </Text>
        )}

        <Markdown style={markdownStyles}>
          {!expanded && isTruncatable
            ? `${text.slice(0, 700).trimEnd()}…`
            : text}
        </Markdown>

        {isTruncatable && (
          <Pressable onPress={() => setExpanded((v) => !v)}>
            <Text
              className="text-xs font-semibold"
              style={{ color: "rgba(255,255,255,0.45)" }}
            >
              {expanded ? i18n.t("misc.seeLess") : i18n.t("misc.seeMore")}
            </Text>
          </Pressable>
        )}
      </VStack>
    </Box>
  );
};

/* ── Export ─────────────────────────────────────────────── */

export default Summary;
